/**
 * Sistema de cola de impresión con IndexedDB
 * Registra clicks de impresión offline y los sincroniza cuando hay conexión
 */

class PrintQueue {
  constructor() {
    this.dbName = 'PrintQueueDB';
    this.storeName = 'prints';
    this.webhookURL = 'https://webhook.blasruiz.ddns.net/9bd3dc4e-f102-4b09-b12c-e99fe4636bcc';
    this.db = null;
    this.syncInProgress = false;
    
    // Inicializar
    this.init();
  }

  /**
   * Inicializa la base de datos IndexedDB
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('Error opening IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        
        // Escuchar cambios de conexión para sincronizar
        window.addEventListener('online', () => {
          console.log('Connection restored, syncing queue...');
          this.syncQueue();
        });
        
        // Sincronizar al iniciar si hay conexión
        if (this.isOnline()) {
          console.log('App started online, checking queue...');
          setTimeout(() => this.syncQueue(), 2000); // Dar tiempo a que cargue todo
        }
        
        // Sincronización periódica cada 30 segundos si hay conexión
        setInterval(() => {
          if (this.isOnline() && !this.syncInProgress) {
            this.syncQueue();
          }
        }, 30000);
        
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Crear object store si no existe
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          
          // Crear índices
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('synced', 'synced', { unique: false });
          
          console.log('Object store created');
        }
      };
    });
  }

  /**
   * Verifica si hay conexión a internet
   */
  isOnline() {
    return navigator.onLine;
  }

  /**
   * Registra un click de impresión
   */
  async recordPrint() {
    const record = {
      timestamp: new Date().toISOString(),
      brandId: window.data?.get('brand'),
      gender: window.data?.get('gender'),
      name: window.data?.get('name'),
      synced: false
    };

    // Siempre guardar en IndexedDB primero (por seguridad)
    console.log('Saving print record to IndexedDB');
    const recordId = await this.saveToQueue(record);
    
    // Si hay conexión, intentar enviar en segundo plano
    if (this.isOnline()) {
      console.log('Online: attempting to send and sync queue');
      // No esperar la respuesta para no bloquear al usuario
      this.sendAndDelete(recordId, record).catch(err => {
        console.warn('Background send failed, will retry on next sync:', err);
      });
    } else {
      console.log('Offline: record saved, will sync when online');
    }
    
    return recordId;
  }

  /**
   * Envía un registro y lo elimina si tiene éxito
   */
  async sendAndDelete(recordId, record) {
    const success = await this.sendToWebhook(record);
    if (success) {
      await this.deleteRecord(recordId);
      console.log('Record sent and deleted from queue:', recordId);
    }
    return success;
  }

  /**
   * Guarda un registro en la cola de IndexedDB
   */
  async saveToQueue(record) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.add(record);

      request.onsuccess = () => {
        console.log('Print record saved to queue with ID:', request.result);
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('Error saving to queue:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Envía un registro al webhook
   */
  async sendToWebhook(record) {
    try {
      // Construir URL con parámetros de query
      const params = new URLSearchParams({
        event: 'print_click',
        timestamp: record.timestamp,
        brandId: record.brandId || '',
        gender: record.gender || '',
        name: record.name || ''
      });
      
      const response = await fetch(`${this.webhookURL}?${params.toString()}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Webhook request failed: ${response.status}`);
      }

      console.log('Print record sent to webhook successfully');
      return true;
    } catch (error) {
      console.error('Error sending to webhook:', error);
      return false;
    }
  }

  /**
   * Obtiene todos los registros pendientes de sincronizar
   */
  async getPendingRecords() {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.getAll();

      request.onsuccess = () => {
        // Filtrar solo los registros no sincronizados
        const allRecords = request.result || [];
        const pendingRecords = allRecords.filter(record => record.synced === false);
        resolve(pendingRecords);
      };

      request.onerror = () => {
        console.error('Error getting pending records:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Marca un registro como sincronizado
   */
  async markAsSynced(id) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.get(id);

      request.onsuccess = () => {
        const record = request.result;
        if (record) {
          record.synced = true;
          const updateRequest = objectStore.put(record);
          
          updateRequest.onsuccess = () => {
            console.log('Record marked as synced:', id);
            resolve();
          };
          
          updateRequest.onerror = () => {
            reject(updateRequest.error);
          };
        } else {
          resolve(); // Record no existe, ya fue sincronizado o eliminado
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Elimina un registro de la cola
   */
  async deleteRecord(id) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.delete(id);

      request.onsuccess = () => {
        console.log('Record deleted:', id);
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Sincroniza la cola de registros pendientes
   */
  async syncQueue() {
    if (this.syncInProgress) {
      console.log('Sync already in progress, skipping...');
      return;
    }

    if (!this.isOnline()) {
      console.log('Still offline, cannot sync');
      return;
    }

    this.syncInProgress = true;

    try {
      const pendingRecords = await this.getPendingRecords();
      
      if (pendingRecords.length === 0) {
        console.log('No pending records to sync');
        return;
      }
      
      console.log(`Starting queue sync... Found ${pendingRecords.length} pending records`);

      for (const record of pendingRecords) {
        try {
          console.log(`Syncing record ${record.id}:`, record);
          const success = await this.sendToWebhook(record);
          
          if (success) {
            // Eliminar registro después de enviar exitosamente
            await this.deleteRecord(record.id);
            console.log(`Successfully synced and deleted record ${record.id}`);
          } else {
            // Marcar como sincronizado fallido (se reintentará en la próxima sincronización)
            console.warn('Failed to sync record:', record.id);
          }
        } catch (error) {
          console.error('Error syncing record:', record.id, error);
        }
      }

      console.log('Queue sync completed');
    } catch (error) {
      console.error('Error during queue sync:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Obtiene estadísticas de la cola
   */
  async getQueueStats() {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);
      const countRequest = objectStore.count();

      countRequest.onsuccess = () => {
        resolve({
          totalRecords: countRequest.result,
          isOnline: this.isOnline(),
          syncInProgress: this.syncInProgress
        });
      };

      countRequest.onerror = () => {
        reject(countRequest.error);
      };
    });
  }
}

// Exportar instancia singleton
const printQueue = new PrintQueue();
export default printQueue;
